import { RRule, Weekday } from 'rrule'

export type Frequency = 'never' | 'daily' | 'weekly' | 'monthly'

export interface ScheduleConfig {
  frequency: Frequency
  interval: number
  weekdays: number[]
  monthDay: number
  hour: number
  minute: number
  count?: number
  /**
   * Optional inclusive start date (`YYYY-MM-DD`) before which the schedule must
   * not run. Encoded as the RRULE `DTSTART` (UTC midnight of the chosen day).
   * Empty/null means "start today".
   */
  startDate?: string | null
}

export interface ScheduleValue {
  rrule: string | null
  timezone: string
}

export const DEFAULT_SCHEDULE_CONFIG: ScheduleConfig = {
  frequency: 'weekly',
  interval: 1,
  weekdays: [0],
  monthDay: 1,
  hour: 9,
  minute: 0,
}

const WEEKDAY_MAP: Record<number, Weekday> = {
  0: RRule.MO,
  1: RRule.TU,
  2: RRule.WE,
  3: RRule.TH,
  4: RRule.FR,
  5: RRule.SA,
  6: RRule.SU,
}

const WEEKDAY_REVERSE_MAP: Record<number, number> = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
}

export function resolveBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/** `YYYY-MM-DD` → UTC-midnight Date, or null when empty/invalid. */
function parseStartDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Date → `YYYY-MM-DD` (UTC), matching {@link parseStartDate}'s encoding. */
function formatStartDate(date: Date | null | undefined): string | null {
  if (!date || Number.isNaN(date.getTime())) return null
  return date.toISOString().slice(0, 10)
}

export function buildRRule(config: ScheduleConfig): string | null {
  if (config.frequency === 'never') return null

  const options: Partial<ConstructorParameters<typeof RRule>[0]> = {
    interval: config.interval,
    byhour: [config.hour],
    byminute: [config.minute],
    bysecond: [0],
  }

  if (config.count && config.count > 0) {
    options.count = config.count
  }

  const dtstart = parseStartDate(config.startDate)
  if (dtstart) {
    options.dtstart = dtstart
  }

  switch (config.frequency) {
    case 'daily':
      options.freq = RRule.DAILY
      break
    case 'weekly':
      options.freq = RRule.WEEKLY
      if (config.weekdays.length > 0) {
        options.byweekday = config.weekdays
          .map((day) => WEEKDAY_MAP[day])
          .filter((wd): wd is Weekday => wd !== undefined)
      }
      break
    case 'monthly':
      options.freq = RRule.MONTHLY
      options.bymonthday = [config.monthDay]
      break
  }

  const rule = new RRule(options as ConstructorParameters<typeof RRule>[0])
  return rule.toString()
}

export function parseRRule(rruleString: string | null | undefined): ScheduleConfig {
  if (!rruleString) return { ...DEFAULT_SCHEDULE_CONFIG }

  try {
    const rule = RRule.fromString(rruleString)
    const options = rule.origOptions

    const config: ScheduleConfig = {
      ...DEFAULT_SCHEDULE_CONFIG,
      interval: options.interval || 1,
      hour: Array.isArray(options.byhour) ? (options.byhour[0] ?? 9) : (options.byhour ?? 9),
      minute: Array.isArray(options.byminute) ? (options.byminute[0] ?? 0) : (options.byminute ?? 0),
      count: options.count ?? undefined,
      startDate: formatStartDate(options.dtstart),
    }

    switch (options.freq) {
      case RRule.DAILY:
        config.frequency = 'daily'
        break
      case RRule.WEEKLY:
        config.frequency = 'weekly'
        if (options.byweekday) {
          const weekdays = Array.isArray(options.byweekday)
            ? options.byweekday
            : [options.byweekday]
          config.weekdays = weekdays
            .map((wd) => {
              if (typeof wd === 'number') return WEEKDAY_REVERSE_MAP[wd]
              if (typeof wd === 'string') {
                return WEEKDAY_REVERSE_MAP[
                  ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].indexOf(wd)
                ]
              }
              return WEEKDAY_REVERSE_MAP[wd.weekday]
            })
            .filter((v): v is number => v !== undefined)
        }
        break
      case RRule.MONTHLY:
        config.frequency = 'monthly'
        if (options.bymonthday) {
          const monthdays = Array.isArray(options.bymonthday)
            ? options.bymonthday
            : [options.bymonthday]
          config.monthDay = monthdays[0] || 1
        }
        break
      default:
        config.frequency = 'never'
    }

    return config
  } catch {
    return { ...DEFAULT_SCHEDULE_CONFIG }
  }
}

export function getNextOccurrences(
  rruleString: string | null | undefined,
  count = 3,
): Date[] {
  if (!rruleString) return []

  try {
    let rule = RRule.fromString(rruleString)
    if (!rule.options.dtstart) {
      rule = new RRule({
        ...rule.origOptions,
        dtstart: new Date(),
      } as ConstructorParameters<typeof RRule>[0])
    }
    // Always preview upcoming runs: a past or future DTSTART shouldn't surface
    // stale occurrences, so walk forward from now.
    const now = new Date()
    const occurrences: Date[] = []
    let cursor = rule.after(now, true)
    while (cursor && occurrences.length < count) {
      occurrences.push(cursor)
      cursor = rule.after(cursor, false)
    }
    return occurrences
  } catch {
    return []
  }
}

export function formatScheduleDescription(
  config: ScheduleConfig,
  labels?: {
    manual?: string
    every?: string
    day?: string
    days?: string
    week?: string
    weeks?: string
    month?: string
    months?: string
    on?: string
    at?: string
    dayOfMonth?: string
    runs?: string
    dayNames?: string[]
    // Singular ("interval === 1") phrases, including the unit. Some languages
    // need gender agreement here that a generic `every` + unit can't express
    // (e.g. German "jede Woche" vs "jeden Tag"). Default to `${every} ${unit}`.
    everyDay?: string
    everyWeek?: string
    everyMonth?: string
  },
): string {
  const l = {
    manual: 'Manual only',
    every: 'every',
    day: 'day',
    days: 'days',
    week: 'week',
    weeks: 'weeks',
    month: 'month',
    months: 'months',
    on: 'on',
    at: 'at',
    dayOfMonth: 'on day',
    runs: 'runs',
    dayNames: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    ...labels,
  }

  if (config.frequency === 'never') return l.manual

  const timeStr = `${config.hour.toString().padStart(2, '0')}:${config.minute.toString().padStart(2, '0')}`
  const isPlural = config.interval > 1

  // Singular uses a unit-specific phrase (gender-aware); plural is the regular
  // "every N <units>" form.
  const everyUnit = (
    singular: string | undefined,
    singularUnit: string,
    pluralUnit: string,
  ): string =>
    isPlural
      ? `${l.every} ${config.interval} ${pluralUnit}`
      : (singular ?? `${l.every} ${singularUnit}`)

  let description = ''
  switch (config.frequency) {
    case 'daily':
      description = `${everyUnit(l.everyDay, l.day, l.days)} ${l.at} ${timeStr}`
      break
    case 'weekly': {
      const days =
        config.weekdays.length > 0
          ? config.weekdays.map((d) => l.dayNames[d]).join(', ')
          : l.dayNames.join(', ')
      description = `${everyUnit(l.everyWeek, l.week, l.weeks)} ${l.on} ${days} ${l.at} ${timeStr}`
      break
    }
    case 'monthly':
      description = `${everyUnit(l.everyMonth, l.month, l.months)} ${l.dayOfMonth} ${config.monthDay} ${l.at} ${timeStr}`
      break
  }

  if (config.count) {
    description += ` (${config.count} ${l.runs})`
  }

  return description
}

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Monday', short: 'Mon' },
  { value: 1, label: 'Tuesday', short: 'Tue' },
  { value: 2, label: 'Wednesday', short: 'Wed' },
  { value: 3, label: 'Thursday', short: 'Thu' },
  { value: 4, label: 'Friday', short: 'Fri' },
  { value: 5, label: 'Saturday', short: 'Sat' },
  { value: 6, label: 'Sunday', short: 'Sun' },
]

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: i.toString().padStart(2, '0'),
}))

export const MINUTE_OPTIONS = [
  { value: 0, label: '00' },
  { value: 15, label: '15' },
  { value: 30, label: '30' },
  { value: 45, label: '45' },
]

export const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: (i + 1).toString(),
}))
